import { useEffect, useEffectEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { Toasts } from '@components/ui/Toasts/Toasts';
import { useToasts } from '@components/ui/Toasts/useToasts';
import { rateLimitedCopy, type IRateLimitedDetail } from '@d-orca/shared/client/apis';
import { RATE_LIMITED_EVENT } from '@apis/rateLimited';

/**
 * The app-wide answer to a 429: one toast saying the server is pacing the user
 * and how long to wait. The failing page shows its own error as before; this
 * explains it. A burst of refused calls (a sweep, several queries at once)
 * raises one toast, not one per call — a new one only after it is gone.
 */
export function RateLimitNotice() {
  const { t } = useTranslation();
  const { toasts, push, dismiss } = useToasts();

  const onRateLimited = useEffectEvent((event: Event) => {
    if (toasts.length > 0) return;
    const copy = rateLimitedCopy((event as CustomEvent<IRateLimitedDetail>).detail);
    push({ kind: 'error', title: t(copy.title), sub: t(copy.sub, { count: copy.count }) });
  });
  useEffect(() => {
    window.addEventListener(RATE_LIMITED_EVENT, onRateLimited);
    return () => window.removeEventListener(RATE_LIMITED_EVENT, onRateLimited);
  }, []); // onRateLimited is stable via useEffectEvent; subscribe once

  return <Toasts toasts={toasts} onDismiss={dismiss} />;
}
